import { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { PageBox } from "@/components/UI/page";
import { ActionHandler } from "@/components/UI/tools";
import { appState } from "@/app/appState";
import {
    ActiveToggle,
    EvalPairSide,
    EvaluationHeader,
    InactiveBadge,
    JudgesTab,
    MasterDetail,
    RankingView,
    ResultsTab,
    TestsTab,
    VoteTab,
    makeEvalApi,
    makeSetActive,
    reportError,
} from "@/app/workspace/evaluation/evaluationShared";

// ---------------------------------------------------------------------------
// Types (mirror backend schemas.chat_test)
// ---------------------------------------------------------------------------

type KvpSet = {
    id: number;
    organisation_id: number;
    name: string | null;
    kvp_overrides: Record<string, string>;
    active: boolean;
};

type KvpKey = { key: string; value: string };

type ContextRow = {
    id: number;
    organisation_id: number;
    name: string | null;
    tag_ids: number[];
    message_count: number;
    messages: SnapshotMessage[] | null;
    active: boolean;
};

type SnapshotMessage = {
    role: string;
    content_json: unknown;
    is_injection?: boolean;
    is_identity?: boolean;
    is_pre_prompt?: boolean;
};

type AgentSet = {
    id: number;
    organisation_id: number;
    agent_id: number;
    agent_name: string | null;
    model_key: string | null;
    active: boolean;
};

type ContextRankings = {
    chat_test_context_id: number;
    context_name: string | null;
    rankings: RankingView;
};

type ChatJudgeResults = {
    judge: string;
    judge_label: string;
    kind: string; // "agent" | "human" | "combined"
    overall: RankingView;
    per_context: ContextRankings[];
    match_count: number;
};

type Progress = {
    prompt_count: number;
    context_count: number;
    model_count: number;
    expected_outputs: number;
    completed_outputs: number;
};

/** A stored assistant turn: {"messages": [{role, content_json}, ...]}. */
type Turn = {
    messages?: { role: string; content_json?: { content?: ContentBlock[] } }[];
};

type ContentBlock = {
    type?: string;
    text?: string;
    name?: string;
    input?: unknown;
    content?: unknown;
    is_error?: boolean;
};

const TABS = [
    "contexts",
    "kvps",
    "agents",
    "tests",
    "judges",
    "vote",
    "results",
] as const;

type Tab = (typeof TABS)[number];

const api = makeEvalApi("/api/chat-tests");

const kvpSetLabel = (p: KvpSet) => p.name?.trim() || `KVP set #${p.id}`;

const contextLabel = (c: { id: number; name: string | null }) =>
    c.name?.trim() || `Context #${c.id}`;

const agentSetLabel = (a: AgentSet) =>
    `${a.agent_name ?? `Agent #${a.agent_id}`} (${a.model_key ?? "?"})`;

/** Disabled competitors stay in the historical rankings, so mark them there. */
const withActiveSuffix = (label: string, active: boolean) =>
    active ? label : `${label} (off)`;

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export const ChatEvaluationPage = observer(() => {
    const isSuperAdmin = appState.user?.is_super_admin;
    const [tab, setTab] = useState<Tab>("contexts");

    if (!isSuperAdmin) {
        return (
            <PageBox>
                <p>{appState.t("settings.unauthorized")}</p>
            </PageBox>
        );
    }

    return (
        <PageBox>
            <div className="flex flex-col gap-2 h-[calc(100vh-8rem)] min-h-[24rem]">
                <EvaluationHeader
                    route="chat_evaluation"
                    tabs={TABS}
                    tab={tab}
                    setTab={setTab}
                />

                <div className="flex-1 min-h-0">
                    {tab === "contexts" && <ContextsTab />}
                    {tab === "kvps" && <KvpsTab />}
                    {tab === "agents" && <AgentsTab />}
                    {tab === "tests" && <TestsTab api={api} />}
                    {tab === "judges" && <JudgesTab api={api} />}
                    {tab === "vote" && <ChatVoteTab />}
                    {tab === "results" && <ChatResultsTab />}
                </div>
            </div>
        </PageBox>
    );
});

// ---------------------------------------------------------------------------
// Contexts tab — conversation snapshots pasted as JSON
// ---------------------------------------------------------------------------

const ContextsTab = observer(() => {
    const [rows, setRows] = useState<ContextRow[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [detail, setDetail] = useState<ContextRow | null>(null);
    const [isNew, setIsNew] = useState(false);
    const [draftName, setDraftName] = useState("");
    const [draftJson, setDraftJson] = useState("");
    const [saving, setSaving] = useState(false);
    const setActive = makeSetActive(api, "context");

    const load = async (preserveId?: number) => {
        try {
            const data = await api<ContextRow[]>("list-contexts", {});
            setRows(data);
            setSelectedId(preserveId ?? (data.length > 0 ? data[0].id : null));
            setIsNew(false);
        } catch (e) {
            reportError(e);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (selectedId == null) {
            setDetail(null);
            return;
        }
        api<ContextRow>("get-context", { id: selectedId })
            .then(setDetail)
            .catch(reportError);
    }, [selectedId]);

    const addNew = () => {
        setSelectedId(null);
        setIsNew(true);
        setDraftName("");
        setDraftJson("");
    };

    const create = async () => {
        let snapshot: unknown;
        try {
            snapshot = JSON.parse(draftJson);
        } catch {
            reportError(new Error("Snapshot is not valid JSON"));
            return;
        }
        setSaving(true);
        try {
            const created = await api<ContextRow>("create-context", {
                name: draftName,
                snapshot,
            });
            await load(created.id);
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id: number) => {
        setSaving(true);
        try {
            await api("delete-context", { id });
            await load();
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (c: ContextRow) => {
        setSaving(true);
        try {
            await setActive(c.id, !c.active);
            // The detail pane is fetched separately, so refresh it explicitly.
            setDetail(await api<ContextRow>("get-context", { id: c.id }));
            await load(c.id);
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <MasterDetail
            addLabel="+ Add context"
            onAdd={addNew}
            items={rows.map((c) => ({
                id: c.id,
                label: contextLabel(c),
                selected: !isNew && c.id === selectedId,
                active: c.active,
                onClick: () => {
                    setSelectedId(c.id);
                    setIsNew(false);
                },
            }))}
            emptyLabel="No contexts"
        >
            {isNew ? (
                <div className="flex flex-col gap-2 h-full min-h-0">
                    <p className="admin-note">
                        Paste a conversation snapshot copied from a chat (the copy button
                        next to a message). Turns precompute on save; a trailing assistant
                        turn is stripped, since that is what the agent must reproduce.
                    </p>
                    <label className="flex flex-col gap-1">
                        <span className="field-label">Name</span>
                        <input
                            className="input input-mono"
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            placeholder={appState.t("eval.contextNamePlaceholder")}
                        />
                    </label>
                    <label className="flex flex-col gap-1 flex-1 min-h-0">
                        <span className="field-label">
                            Snapshot JSON — {"{"}"tag_ids": [...], "messages": [...]{"}"}
                        </span>
                        <textarea
                            className="textarea input-mono flex-1 min-h-[16rem]"
                            value={draftJson}
                            onChange={(e) => setDraftJson(e.target.value)}
                            placeholder='{"tag_ids": [12], "messages": [{"role": "user", "content_json": {...}}]}'
                        />
                    </label>
                    <div className="flex flex-row gap-2 shrink-0">
                        <ActionHandler title={appState.t("common.save")} funq={create} disabled={saving} className="btn-ghost btn-mono">
                            {saving ? appState.t("common.saving") : appState.t("common.save")}
                        </ActionHandler>
                    </div>
                </div>
            ) : detail ? (
                <div className="flex flex-col gap-2 h-full min-h-0">
                    <div className="flex flex-row items-center justify-between gap-2">
                        <h2 className="admin-heading flex flex-row items-center gap-2 min-w-0">
                            <span className="truncate">{contextLabel(detail)}</span>
                            {!detail.active && <InactiveBadge />}
                        </h2>
                        <div className="flex flex-row items-center gap-2 shrink-0">
                            <ActiveToggle
                                active={detail.active}
                                onToggle={() => toggleActive(detail)}
                                disabled={saving}
                            />
                            <ActionHandler
                                title={appState.t("common.delete")}
                                funq={() => remove(detail.id)}
                                disabled={saving}
                                className="btn-ghost btn-mono"
                            >
                                {appState.t("common.delete")}
                            </ActionHandler>
                        </div>
                    </div>
                    {!detail.active && (
                        <p className="admin-note">
                            Disabled — no new turns or matches are generated for this
                            context. Existing ones are kept.
                        </p>
                    )}
                    <p className="admin-note">
                        {detail.message_count} messages · tag scope [
                        {detail.tag_ids.join(", ")}]
                    </p>
                    <textarea
                        readOnly
                        className="textarea input-mono flex-1 min-h-[16rem]"
                        value={JSON.stringify(detail.messages ?? [], null, 2)}
                    />
                </div>
            ) : (
                <p className="empty-state">
                    Select a context, or add a new one.
                </p>
            )}
        </MasterDetail>
    );
});

// ---------------------------------------------------------------------------
// KVPs tab — arbitrary key→value overrides applied on top of the live kvp table
// ---------------------------------------------------------------------------

type OverrideRow = { key: string; value: string };

const KvpsTab = observer(() => {
    const [rows, setRows] = useState<KvpSet[]>([]);
    const [keys, setKeys] = useState<KvpKey[]>([]);
    const [selectedId, setSelectedId] = useState<number | null>(null);
    const [isNew, setIsNew] = useState(false);
    const [draftName, setDraftName] = useState("");
    const [draftOverrides, setDraftOverrides] = useState<OverrideRow[]>([]);
    const [saving, setSaving] = useState(false);
    const setActive = makeSetActive(api, "prompt");

    const load = async (preserveId?: number) => {
        try {
            const [sets, kvpKeys] = await Promise.all([
                api<KvpSet[]>("list-prompts", {}),
                api<KvpKey[]>("list-kvp-keys", {}),
            ]);
            setRows(sets);
            setKeys(kvpKeys);
            setSelectedId(preserveId ?? (sets.length > 0 ? sets[0].id : null));
            setIsNew(false);
        } catch (e) {
            reportError(e);
        }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const selected = rows.find((x) => x.id === selectedId) ?? null;

    // Load the selected set into the editor whenever the selection changes.
    useEffect(() => {
        if (isNew || !selected) return;
        setDraftName(selected.name ?? "");
        setDraftOverrides(
            Object.entries(selected.kvp_overrides ?? {}).map(([key, value]) => ({
                key,
                value,
            }))
        );
    }, [selectedId, isNew, selected]);

    const addNew = () => {
        setSelectedId(null);
        setIsNew(true);
        setDraftName("");
        setDraftOverrides([]);
    };

    const overridesObject = () =>
        Object.fromEntries(
            draftOverrides
                .filter((r) => r.key.trim() !== "")
                .map((r) => [r.key.trim(), r.value])
        );

    const save = async () => {
        setSaving(true);
        try {
            if (isNew) {
                const created = await api<KvpSet>("create-prompt", {
                    name: draftName,
                    kvp_overrides: overridesObject(),
                });
                await load(created.id);
            } else if (selected) {
                await api<KvpSet>("update-prompt", {
                    id: selected.id,
                    name: draftName,
                    kvp_overrides: overridesObject(),
                });
                await load(selected.id);
                appState.setModal({
                    title: appState.t("eval.regeneratingTurns"),
                    body: appState.t("eval.regeneratingTurnsBody"),
                });
            }
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id: number) => {
        setSaving(true);
        try {
            await api("delete-prompt", { id });
            await load();
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (p: KvpSet) => {
        setSaving(true);
        try {
            await setActive(p.id, !p.active);
            await load(p.id);
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const setRow = (i: number, patch: Partial<OverrideRow>) =>
        setDraftOverrides((prev) =>
            prev.map((r, j) => (j === i ? { ...r, ...patch } : r))
        );

    const editing = isNew || selected != null;

    return (
        <MasterDetail
            addLabel="+ Add KVP set"
            onAdd={addNew}
            items={rows.map((p) => ({
                id: p.id,
                label: kvpSetLabel(p),
                selected: !isNew && p.id === selectedId,
                active: p.active,
                onClick: () => {
                    setSelectedId(p.id);
                    setIsNew(false);
                },
            }))}
            emptyLabel="No KVP sets"
        >
            {editing ? (
                <div className="flex flex-col gap-2 h-full min-h-0">
                    <div className="flex flex-row items-center justify-between gap-2">
                        <p className="admin-note">
                            Overrides are merged over the live kvp table for this
                            competitor. An empty set means production behaviour.
                            {!isNew && selected && !selected.active && (
                                <> Disabled — no new turns or matches are generated.</>
                            )}
                        </p>
                        {!isNew && selected && (
                            <div className="flex flex-row items-center gap-2 shrink-0">
                                <ActiveToggle
                                    active={selected.active}
                                    onToggle={() => toggleActive(selected)}
                                    disabled={saving}
                                />
                                <ActionHandler
                                    title={appState.t("common.delete")}
                                    funq={() => remove(selected.id)}
                                    disabled={saving}
                                    className="btn-ghost btn-mono"
                                >
                                    {appState.t("common.delete")}
                                </ActionHandler>
                            </div>
                        )}
                    </div>
                    <label className="flex flex-col gap-1">
                        <span className="field-label">Name</span>
                        <input
                            className="input input-mono"
                            value={draftName}
                            onChange={(e) => setDraftName(e.target.value)}
                            placeholder={appState.t("eval.kvpNamePlaceholder")}
                        />
                    </label>

                    <datalist id="chat-eval-kvp-keys">
                        {keys.map((k) => (
                            <option key={k.key} value={k.key} />
                        ))}
                    </datalist>

                    <div className="flex-1 min-h-0 overflow-y-auto flex flex-col gap-3">
                        {draftOverrides.map((row, i) => (
                            <div key={i} className="panel shrink-0">
                                <div className="flex flex-row gap-2 items-center">
                                    <input
                                        className="input input-mono"
                                        list="chat-eval-kvp-keys"
                                        value={row.key}
                                        onChange={(e) => setRow(i, { key: e.target.value })}
                                        placeholder={appState.t("eval.kvpKeyPlaceholder")}
                                    />
                                    <button
                                        type="button"
                                        title={appState.t("eval.loadProductionValue")}
                                        onClick={() =>
                                            setRow(i, {
                                                value:
                                                    keys.find((k) => k.key === row.key)?.value ??
                                                    row.value,
                                            })
                                        }
                                        className="link-muted shrink-0"
                                    >
                                        load current
                                    </button>
                                    <button
                                        type="button"
                                        onClick={() =>
                                            setDraftOverrides((prev) =>
                                                prev.filter((_, j) => j !== i)
                                            )
                                        }
                                        className="link-danger shrink-0"
                                    >
                                        remove
                                    </button>
                                </div>
                                <textarea
                                    className="textarea input-mono min-h-[6rem]"
                                    value={row.value}
                                    onChange={(e) => setRow(i, { value: e.target.value })}
                                    placeholder={appState.t("eval.overrideValue")}
                                />
                            </div>
                        ))}
                        {draftOverrides.length === 0 && (
                            <p className="empty-state">
                                No overrides — this set reproduces production prompts.
                            </p>
                        )}
                    </div>

                    <div className="flex flex-row gap-2 shrink-0">
                        <button
                            type="button"
                            onClick={() =>
                                setDraftOverrides((prev) => [...prev, { key: "", value: "" }])
                            }
                            className="btn btn-soft btn-sm btn-mono"
                        >
                            + Add override
                        </button>
                        <ActionHandler title={appState.t("common.save")} funq={save} disabled={saving} className="btn-ghost btn-mono">
                            {saving ? appState.t("common.saving") : appState.t("common.save")}
                        </ActionHandler>
                    </div>
                </div>
            ) : (
                <p className="empty-state">
                    Select a KVP set, or add a new one.
                </p>
            )}
        </MasterDetail>
    );
});

// ---------------------------------------------------------------------------
// Agents tab — the agents that compete
// ---------------------------------------------------------------------------

const AgentsTab = observer(() => {
    const [rows, setRows] = useState<AgentSet[]>([]);
    const [agentId, setAgentId] = useState<number | null>(null);
    const [saving, setSaving] = useState(false);
    const setActive = makeSetActive(api, "model");

    const load = async () => {
        try {
            setRows(await api<AgentSet[]>("list-models", {}));
        } catch (e) {
            reportError(e);
        }
    };

    useEffect(() => {
        load();
        appState.getAgents({ pageNumber: 0, search: "" });
    }, []);

    useEffect(() => {
        if (agentId == null && appState.agents.length > 0) {
            setAgentId(appState.agents[0].id);
        }
    }, [agentId]);

    const add = async () => {
        if (agentId == null) return;
        setSaving(true);
        try {
            await api<AgentSet>("create-model", { agent_id: agentId });
            await load();
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const remove = async (id: number) => {
        setSaving(true);
        try {
            await api("delete-model", { id });
            await load();
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    const toggleActive = async (a: AgentSet) => {
        setSaving(true);
        try {
            await setActive(a.id, !a.active);
            await load();
        } catch (e) {
            reportError(e);
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="panel gap-2 h-full">
            <p className="admin-note">
                An agent contributes its identity, pre-prompt and LLM model. Adding one
                multiplies the turn pool and triggers generation. There is no implicit
                production baseline — only the agents listed here compete.
            </p>
            <div className="flex flex-row gap-2 items-end shrink-0 flex-wrap">
                <label className="flex flex-col gap-1">
                    <span className="field-label">Agent</span>
                    <select
                        className="select input-mono"
                        value={agentId ?? ""}
                        onChange={(e) => setAgentId(parseInt(e.target.value, 10))}
                    >
                        {appState.agents.map((a) => (
                            <option key={a.id} value={a.id}>
                                {a.name}
                            </option>
                        ))}
                    </select>
                </label>
                <ActionHandler title={appState.t("eval.addAgent")} funq={add} disabled={saving} className="btn-ghost btn-mono">
                    {saving ? appState.t("common.saving") : appState.t("eval.addAgent")}
                </ActionHandler>
            </div>
            <div className="flex-1 min-h-0 overflow-y-auto">
                <table className="data-table">
                    <thead>
                        <tr>
                            <th>#</th>
                            <th>Agent</th>
                            <th>Model</th>
                            <th></th>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {rows.map((a) => (
                            <tr key={a.id} className={a.active ? "" : "data-table-row-inactive"}>
                                <td>
                                    <span className="flex flex-row items-center gap-1">
                                        #{a.id}
                                        {!a.active && <InactiveBadge />}
                                    </span>
                                </td>
                                <td className="truncate max-w-[20rem]">
                                    {a.agent_name ?? `Agent #${a.agent_id}`}
                                </td>
                                <td>{a.model_key ?? "?"}</td>
                                <td className="text-right">
                                    <button
                                        type="button"
                                        disabled={saving}
                                        onClick={() => toggleActive(a)}
                                        className="link-muted"
                                    >
                                        {a.active ? "disable" : "enable"}
                                    </button>
                                </td>
                                <td className="text-right">
                                    <button type="button" onClick={() => remove(a.id)} className="link-danger">
                                        remove
                                    </button>
                                </td>
                            </tr>
                        ))}
                        {rows.length === 0 && (
                            <tr>
                                <td colSpan={5} className="empty-state">
                                    No agents — nothing can be generated yet.
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
});

// ---------------------------------------------------------------------------
// Turn rendering — flattens a stored assistant turn for human judging
// ---------------------------------------------------------------------------

const TOOL_RESULT_LIMIT = 2000;

const TurnView = ({ turn }: { turn: Turn }) => {
    const blocks: { kind: string; label?: string; body: string }[] = [];

    for (const msg of turn.messages ?? []) {
        for (const block of msg.content_json?.content ?? []) {
            switch (block.type) {
                case "text":
                    blocks.push({ kind: "text", body: block.text ?? "" });
                    break;
                case "tool_use":
                    blocks.push({
                        kind: "tool_use",
                        label: `tool call: ${block.name}`,
                        body: JSON.stringify(block.input ?? {}, null, 2),
                    });
                    break;
                case "tool_result": {
                    const raw =
                        typeof block.content === "string"
                            ? block.content
                            : JSON.stringify(block.content ?? "", null, 2);
                    blocks.push({
                        kind: "tool_result",
                        label: `tool result: ${block.name}${block.is_error ? " (error)" : ""}`,
                        body:
                            raw.length > TOOL_RESULT_LIMIT
                                ? `${raw.slice(0, TOOL_RESULT_LIMIT)} …[truncated]`
                                : raw,
                    });
                    break;
                }
                default:
                    break;
            }
        }
    }

    if (blocks.length === 0) {
        return <span className="hint">Empty turn</span>;
    }

    return (
        <div className="flex flex-col gap-2">
            {blocks.map((b, i) => (
                <div key={i}>
                    {b.label && (
                        <div className="label-xs">
                            {b.label}
                        </div>
                    )}
                    <div
                        className={
                            b.kind === "text"
                                ? "whitespace-pre-wrap"
                                : "turn-tool-output"
                        }
                    >
                        {b.body}
                    </div>
                </div>
            ))}
        </div>
    );
};

// ---------------------------------------------------------------------------
// Vote + Results — shared components bound to chat terminology
// ---------------------------------------------------------------------------

const ChatVoteTab = () => (
    <VoteTab
        api={api}
        contextField="chat_test_context_id"
        outputNoun="Turn"
        describeContext={(pair) =>
            (pair.context_name as string | null) ??
            `Context #${pair.chat_test_context_id}`
        }
        renderOutput={(side: EvalPairSide) => (
            <TurnView turn={(side.output ?? {}) as Turn} />
        )}
    />
);

const ChatResultsTab = () => (
    <ResultsTab<ChatJudgeResults, Progress>
        api={api}
        promptHeader="KVP set"
        modelHeader="Agent"
        loadLabels={async () => {
            const [kvpSets, agentSets] = await Promise.all([
                api<KvpSet[]>("list-prompts", {}),
                api<AgentSet[]>("list-models", {}),
            ]);
            return {
                promptNames: Object.fromEntries(
                    kvpSets.map((p) => [
                        p.id,
                        withActiveSuffix(kvpSetLabel(p), p.active),
                    ])
                ),
                modelNames: Object.fromEntries(
                    agentSets.map((a) => [
                        a.id,
                        withActiveSuffix(agentSetLabel(a), a.active),
                    ])
                ),
            };
        }}
        perContext={(jr) =>
            jr.per_context.map((c) => ({
                key: c.chat_test_context_id,
                label: c.context_name ?? `Context #${c.chat_test_context_id}`,
                rankings: c.rankings,
            }))
        }
        progressLine={(p, matchCount) =>
            `Turns cached: ${p.completed_outputs}/${p.expected_outputs}` +
            ` (${p.prompt_count} KVP sets × ${p.context_count} contexts × ${p.model_count} agents)` +
            (matchCount != null ? ` · matches: ${matchCount}` : "")
        }
    />
);
